import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Box, IconButton, MenuItem, Select, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LANG_TEXT } from '../../constants/language';
import { useStyles } from './styles';

type TimeUnit = 'minute' | 'hour' | 'day';
type PickerType = 'none' | 'time';

type TimeUnitInputProps = {
  /** Controls the second field (text #2 + picker). */
  typeProp?: PickerType;
  /** Left label text */
  label1?: string;
  /** Right label text (only used when typeProp !== 'none') */
  label2?: string;
  /** Initial numeric value (uncontrolled) */
  defaultValue?: number;
  /** Initial unit (uncontrolled) */
  defaultUnit?: TimeUnit;
  /** Initial picker value (uncontrolled) */
  defaultPickerValue?: string;
  /** Called when any value changes */
  onChange?: (next: { value: number; unit: TimeUnit; pickerValue?: string }) => void;
  allowMinute?: boolean;
  allowHour?: boolean;
  allowDay?: boolean;
  limit?: { min: number; max: number; step?: number } | null;
};

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

export default function TimeUnitInput(props: TimeUnitInputProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  const {
    typeProp = 'time',
    label1,
    label2,
    defaultValue = 30,
    defaultUnit = 'day',
    defaultPickerValue = '00:00',
    onChange,
    allowMinute = true,
    allowHour = true,
    allowDay = true,
    limit = null,
  } = props;

  // Determine the first available unit
  const getFirstAvailableUnit = (): TimeUnit => {
    if (allowMinute) return 'minute';
    if (allowHour) return 'hour';
    if (allowDay) return 'day';
    return 'minute'; // fallback
  };

  // Check if a unit is allowed
  const isUnitAllowed = (unit: TimeUnit): boolean => {
    switch (unit) {
      case 'minute':
        return allowMinute;
      case 'hour':
        return allowHour;
      case 'day':
        return allowDay;
      default:
        return false;
    }
  };

  // Get effective default unit (fallback if the default is not allowed)
  const getEffectiveDefaultUnit = (): TimeUnit => {
    if (isUnitAllowed(defaultUnit)) {
      return defaultUnit;
    }
    return getFirstAvailableUnit();
  };

  const effectiveDefaultUnit = getEffectiveDefaultUnit();

  const displayLabel1 = label1 ?? t(LANG_TEXT.SINCE);
  const displayLabel2 = label2 ?? t(LANG_TEXT.PRE_ORDER_ACCEPTANCE_AT);

  const [unit, setUnit] = useState<TimeUnit>(effectiveDefaultUnit);
  const [value, setValue] = useState<number>(defaultValue);
  const [valueString, setValueString] = useState<string>(String(defaultValue));
  const [pickerValue, setPickerValue] = useState<string>(defaultPickerValue);
  const [timePickerValue, setTimePickerValue] = useState<Dayjs | null>(() => {
    const [hours, minutes] = defaultPickerValue.split(':');
    return dayjs().hour(parseInt(hours, 10)).minute(parseInt(minutes, 10));
  });

  useEffect(() => {
    setValue(defaultValue);
    setValueString(String(defaultValue));
    setUnit(getEffectiveDefaultUnit());
    setPickerValue(defaultPickerValue);
    const [hours, minutes] = defaultPickerValue.split(':');
    setTimePickerValue(dayjs().hour(parseInt(hours, 10)).minute(parseInt(minutes, 10)));
  }, [defaultValue, defaultUnit, defaultPickerValue, allowMinute, allowHour, allowDay]);

  // Get limits for each unit type
  const getLimits = (unitType: TimeUnit) => {
    if (limit) return { min: limit.min, max: limit.max, step: limit.step || 1 };
    switch (unitType) {
      case 'minute':
        return { min: 0, max: 59, step: 1 };
      case 'hour':
        return { min: 0, max: 23, step: 1 };
      case 'day':
        return { min: 0, max: 30, step: 1 };
      default:
        return { min: 0, max: 9999, step: 1 };
    }
  };

  const limits = useMemo(() => getLimits(unit), [unit, limit]);

  const emit = (next: { value: number; unit: TimeUnit; pickerValue?: string }) => {
    onChange?.(next);
  };

  const setClampedValue = (next: number) => {
    const clamped = clamp(next, limits.min, limits.max);
    setValue(clamped);
    setValueString(String(clamped));
    emit({ value: clamped, unit, pickerValue: typeProp === 'none' ? undefined : pickerValue });
  };

  const handleUnitChange = (nextUnit: TimeUnit) => {
    setUnit(nextUnit);
    const nextLimits = getLimits(nextUnit);
    const clamped = clamp(value, nextLimits.min, nextLimits.max);

    if (clamped !== value) {
      setValue(clamped);
    }

    emit({ value: clamped, unit: nextUnit, pickerValue: typeProp === 'none' ? undefined : pickerValue });
  };

  const handleTimePickerChange = (newValue: Dayjs | null) => {
    setTimePickerValue(newValue);
    if (newValue) {
      const timeString = newValue.format('HH:mm');
      setPickerValue(timeString);
      emit({ value, unit, pickerValue: timeString });
    }
  };

  const commitValueFromString = () => {
    const parsed = parseInt(valueString, 10);
    const next = Number.isNaN(parsed) ? limits.min : parsed;
    setClampedValue(next);
  };

  const handleValueInputChange = (next: string) => {
    if (next === '' || /^\d+$/.test(next)) {
      setValueString(next);
    }
  };

  const handleValueKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      commitValueFromString();
    }
  };

  return (
    <Box className={classes.timeUnitInput}>
      {displayLabel1 && <Typography className={classes.label}>{displayLabel1}</Typography>}

      <Box
        sx={{
          width: 50,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#fff',
          borderRadius: '8px',
          border: '1px solid #E6E9EE',
          textAlign: 'center',
          fontSize: 14,
        }}
      >
        <input
          value={valueString}
          onChange={(e) => handleValueInputChange(e.target.value)}
          onBlur={commitValueFromString}
          onKeyDown={handleValueKeyDown}
          inputMode="numeric"
          pattern="\d*"
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            outline: 'none',
            textAlign: 'center',
            fontSize: 14,
            background: 'transparent',
          }}
        />
      </Box>
      <Box className={classes.buttonGroup}>
        <IconButton type="button" aria-label="increase" onClick={() => setClampedValue(value + limits.step)} className={classes.iconButton} size="small">
          <KeyboardArrowUp />
        </IconButton>
        <IconButton type="button" aria-label="decrease" onClick={() => setClampedValue(value - limits.step)} className={classes.iconButton} size="small">
          <KeyboardArrowDown />
        </IconButton>
      </Box>
      <Select variant="outlined" value={unit} onChange={(e) => handleUnitChange(e.target.value as TimeUnit)} className={classes.select} sx={{ minWidth: 110 }}>
        {allowMinute && (
          <MenuItem key="minute" value="minute">
            {value === 1 ? `${t(LANG_TEXT.MINUTE)}` : `${t(LANG_TEXT.MINUTES)}`}
          </MenuItem>
        )}
        {allowHour && (
          <MenuItem key="hour" value="hour">
            {value === 1 ? `${t(LANG_TEXT.HOUR)}` : `${t(LANG_TEXT.HOURS)}`}
          </MenuItem>
        )}
        {allowDay && (
          <MenuItem key="day" value="day">
            {value === 1 ? `${t(LANG_TEXT.DAY)}` : `${t(LANG_TEXT.DAYS)}`}
          </MenuItem>
        )}
      </Select>

      {typeProp !== 'none' && (
        <>
          {displayLabel2 && <Typography className={classes.label}>{displayLabel2}</Typography>}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TimePicker
              value={timePickerValue}
              onChange={handleTimePickerChange}
              ampm={false}
              className={classes.timeInput}
              slotProps={{
                textField: {
                  variant: 'outlined',
                },
              }}
            />
          </LocalizationProvider>
        </>
      )}
    </Box>
  );
}
